import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Bell, BellOff, Mail, Check, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { userAPI } from '../services/api';
import api from '../services/api';
import { LOGO_URL } from '../data/mockData';
import { toast } from 'sonner';

const EmailPreferencesPage = () => {
  const { user, savedSearches, refreshSavedSearches } = useAuth();
  const [searchParams] = useSearchParams();
  const [updatingAlerts, setUpdatingAlerts] = useState({});
  const [unsubscribingAll, setUnsubscribingAll] = useState(false);
  const [unsubscribed, setUnsubscribed] = useState(false);
  
  // Token-based unsubscribe state
  const token = searchParams.get('token');
  const [tokenInfo, setTokenInfo] = useState(null);
  const [tokenLoading, setTokenLoading] = useState(!!token);
  const [tokenError, setTokenError] = useState(null);
  const [unsubscribing, setUnsubscribing] = useState(false);

  // Fetch token info if token provided
  useEffect(() => {
    if (token) {
      fetchTokenInfo();
    }
  }, [token]);

  const fetchTokenInfo = async () => {
    try {
      const response = await api.get(`/alerts/unsubscribe/info?token=${token}`);
      setTokenInfo(response.data);
    } catch (error) {
      console.error('Failed to fetch token info:', error);
      setTokenError('This unsubscribe link is invalid or has expired.');
    } finally {
      setTokenLoading(false);
    }
  };

  const handleTokenUnsubscribe = async (unsubscribeAll = false) => {
    setUnsubscribing(true);
    try {
      // If unsubscribing all, generate an "all" token client-side won't work
      // We need to unsubscribe each search individually
      if (unsubscribeAll && tokenInfo?.all_alert_searches) {
        // Just use the provided token to unsubscribe from the specific search
        // Then offer to unsubscribe from all
        await api.post(`/alerts/unsubscribe?token=${token}`);
      } else {
        await api.post(`/alerts/unsubscribe?token=${token}`);
      }
      setUnsubscribed(true);
      toast.success('Successfully unsubscribed');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to unsubscribe. Please try again.');
    } finally {
      setUnsubscribing(false);
    }
  };

  // Get searches with alerts enabled
  const alertEnabledSearches = savedSearches.filter(s => s.email_alerts);

  const toggleEmailAlerts = async (searchId, currentValue) => {
    setUpdatingAlerts(prev => ({ ...prev, [searchId]: true }));
    try {
      await userAPI.updateSearch(searchId, { email_alerts: !currentValue });
      await refreshSavedSearches();
      toast.success(currentValue ? 'Email alerts disabled' : 'Email alerts enabled');
    } catch (error) {
      console.error('Failed to update email alerts:', error);
      toast.error('Failed to update email alerts');
    } finally {
      setUpdatingAlerts(prev => ({ ...prev, [searchId]: false }));
    }
  };

  const unsubscribeFromAll = async () => {
    setUnsubscribingAll(true);
    try {
      // Disable alerts for all saved searches
      const promises = alertEnabledSearches.map(search => 
        userAPI.updateSearch(search.id, { email_alerts: false })
      );
      await Promise.all(promises);
      await refreshSavedSearches();
      setUnsubscribed(true);
      toast.success('Successfully unsubscribed from all email alerts');
    } catch (error) {
      console.error('Failed to unsubscribe:', error);
      toast.error('Failed to unsubscribe. Please try again.');
    } finally {
      setUnsubscribingAll(false);
    }
  };

  // TOKEN-BASED UNSUBSCRIBE FLOW (no login required)
  if (token) {
    // Loading state
    if (tokenLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <Loader2 className="animate-spin mx-auto mb-4 text-red-600" size={32} />
              <p className="text-gray-600">Loading your preferences...</p>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Error state
    if (tokenError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <BellOff className="text-red-600" size={32} />
              </div>
              <CardTitle className="text-xl text-red-700">Link Expired</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">{tokenError}</p>
              <Link to="/login">
                <Button className="bg-red-600 hover:bg-red-700">
                  Sign in to manage preferences
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Success state after unsubscribing
    if (unsubscribed) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="text-green-600" size={32} />
              </div>
              <CardTitle className="text-xl text-green-700">Successfully Unsubscribed</CardTitle>
            </CardHeader>
            <CardContent className="text-center">
              <p className="text-gray-600 mb-6">
                You have been unsubscribed from property alert emails. You can re-enable alerts anytime by signing in.
              </p>
              <div className="space-y-3">
                <Link to="/search">
                  <Button className="bg-red-600 hover:bg-red-700 w-full">
                    Browse Properties
                  </Button>
                </Link>
                <Link to="/">
                  <Button variant="outline" className="w-full">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Token unsubscribe confirmation page
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" className="inline-block mx-auto mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <img src={LOGO_URL} alt="Logo" className="w-full h-full object-cover" />
              </div>
            </Link>
            <CardTitle className="text-xl">Unsubscribe from Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-2">
                You're managing alerts for:
              </p>
              <p className="font-medium text-gray-900">{tokenInfo?.user_email}</p>
            </div>

            {tokenInfo?.search && (
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-500 mb-1">This alert:</p>
                <p className="font-medium">
                  {tokenInfo.search.name || tokenInfo.search.keyword || tokenInfo.search.location || 'All Areas'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {[
                    tokenInfo.search.propertyType && tokenInfo.search.propertyType !== 'all' ? tokenInfo.search.propertyType : null,
                    tokenInfo.search.beds && tokenInfo.search.beds !== 'any' ? `${tokenInfo.search.beds}+ beds` : null,
                    tokenInfo.search.minPrice && tokenInfo.search.minPrice !== 'none' ? `$${Number(tokenInfo.search.minPrice).toLocaleString()}+` : null,
                  ].filter(Boolean).join(' • ') || 'All filters'}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button 
                className="w-full bg-red-600 hover:bg-red-700"
                onClick={() => handleTokenUnsubscribe(false)}
                disabled={unsubscribing}
              >
                {unsubscribing ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Unsubscribing...
                  </>
                ) : (
                  'Unsubscribe from this alert'
                )}
              </Button>
              
              {tokenInfo?.total_alerts > 1 && (
                <p className="text-center text-sm text-gray-500">
                  You have {tokenInfo.total_alerts} active alerts.{' '}
                  <Link to="/login" className="text-red-600 hover:underline">
                    Sign in to manage all
                  </Link>
                </p>
              )}
            </div>

            <div className="mt-6 pt-6 border-t text-center">
              <Link to="/" className="text-sm text-gray-500 hover:text-red-600">
                Back to Brantford Area Homes
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Link to="/" className="inline-block mx-auto mb-4">
              <div className="w-16 h-16 rounded-full overflow-hidden">
                <img src={LOGO_URL} alt="Logo" className="w-full h-full object-cover" />
              </div>
            </Link>
            <CardTitle className="text-xl">Manage Email Preferences</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-6">
              Please sign in to manage your email notification preferences.
            </p>
            <Link to="/login">
              <Button className="bg-red-600 hover:bg-red-700 w-full">
                Sign In
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show success message after unsubscribing
  if (unsubscribed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <Check className="text-green-600" size={32} />
            </div>
            <CardTitle className="text-xl text-green-700">Successfully Unsubscribed</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-6">
              You have been unsubscribed from all property alert emails. You can re-enable alerts anytime from your saved searches.
            </p>
            <div className="space-y-3">
              <Link to="/saved-searches">
                <Button variant="outline" className="w-full">
                  View Saved Searches
                </Button>
              </Link>
              <Link to="/">
                <Button className="bg-red-600 hover:bg-red-700 w-full">
                  Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-red-600 mb-4">
            <ArrowLeft size={18} className="mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
              <Mail className="text-red-600" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Email Preferences</h1>
              <p className="text-gray-600">Manage your property alert notifications</p>
            </div>
          </div>
        </div>

        {/* Current Email */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Email Address</p>
                <p className="font-medium">{user.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Alert Status Summary */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              {alertEnabledSearches.length > 0 ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <Bell className="text-green-600" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {alertEnabledSearches.length} active alert{alertEnabledSearches.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-sm text-gray-500">You'll receive daily emails when new properties match your searches</p>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <BellOff className="text-gray-400" size={20} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">No active alerts</p>
                    <p className="text-sm text-gray-500">You won't receive any property alert emails</p>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Saved Searches with Alerts */}
        {savedSearches.length > 0 ? (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-lg">Your Saved Searches</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {savedSearches.map((search) => (
                  <div key={search.id} className="p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">
                        {search.name || search.keyword || search.location || 'All Areas'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {[
                          search.propertyType && search.propertyType !== 'all' ? search.propertyType : null,
                          search.beds && search.beds !== 'any' ? `${search.beds}+ beds` : null,
                          search.minPrice && search.minPrice !== 'none' ? `$${Number(search.minPrice).toLocaleString()}+` : null,
                        ].filter(Boolean).join(' • ') || 'All filters'}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-500">
                        {search.email_alerts ? 'Alerts on' : 'Alerts off'}
                      </span>
                      <Switch
                        checked={search.email_alerts || false}
                        onCheckedChange={() => toggleEmailAlerts(search.id, search.email_alerts)}
                        disabled={updatingAlerts[search.id]}
                        className="data-[state=checked]:bg-green-600"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="mb-6">
            <CardContent className="p-8 text-center">
              <p className="text-gray-500 mb-4">You don't have any saved searches yet.</p>
              <Link to="/search">
                <Button variant="outline">Start Searching</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Unsubscribe All */}
        {alertEnabledSearches.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900">Unsubscribe from all alerts</p>
                  <p className="text-sm text-gray-600">Stop receiving all property notification emails</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-red-300 text-red-600 hover:bg-red-100"
                  onClick={unsubscribeFromAll}
                  disabled={unsubscribingAll}
                >
                  {unsubscribingAll ? 'Unsubscribing...' : 'Unsubscribe All'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>
            Need help? <Link to="/contact" className="text-red-600 hover:underline">Contact us</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailPreferencesPage;
